import React, { useState, useEffect } from 'react';
import { Button, ButtonGroup, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { downloadCsv } from '../utils/downloadCsv';
import HistoryReportDialog from './HistoryReportDialog';
import axios from 'axios';

/**
 * ReportsMenu renders the "Export" button and dropdown for fleet-related CSV reports.
 * It supports exporting vehicles, service providers, and a configurable maintenance history
 * report that allows you to see all the service records of each vehicle.
 * 
 * @param {Object} props
 * @param {string} props.API_BASE - Base URL for the backend API.
 * @param {string} props.token - Auth token used for report downloads.
 * @param {Array<Object>} props.vehicles - Vehicle list passed into the history dialog for scoping.
 *  
 * @returns {JSX.Element}
 */
export default function ReportsMenu({ API_BASE, token, vehicles }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/providers`, {
          headers: { Authorization: `Bearer ${String(token ?? '').trim()}` }
        });
        setProviders(data?.rows || []);
      } catch (e) {
        setProviders([]);
      }
    };
    fetchProviders();
  }, [API_BASE, token]);

  const open = Boolean(anchorEl);
  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  /**
   * Triggers a CSV export of vehicle data with optional query parameters.
   * 
   * @param {Object} [params={}] - Query parameters used to filter the vehicle report. 
   */
  const exportVehicles = async (params = {}) => {
    await downloadCsv({
      baseUrl: API_BASE,
      token,
      path: '/api/reports/vehicles.csv',
      params,
      filename: 'vehicles.csv',
    });
  };

  /**
   * Triggers a CSV export of service providers.
   */
  const exportProviders = async () => {
    await downloadCsv({
      baseUrl: API_BASE,
      token,
      path: '/api/reports/providers.csv',
      filename: 'providers.csv',
    });
  };

  /**
   * Triggers a CSV export of maintenance history using parameters from the history dialog.
   * 
   * @param {Object} params - Query parameters describing date range, vehicle scope, provider, and notes inclusion.
   */
  const exportHistory = async (params) => {
    await downloadCsv({
      baseUrl: API_BASE,
      token,
      path: '/api/reports/history.csv',
      params,
      filename: 'maintenance-history.csv',
    });
  };

  return (
    <>
      <ButtonGroup variant="contained" sx={{ height: 36.5 }}>
        <Button startIcon={<FileDownloadIcon />} onClick={() => exportVehicles()} sx={{ textTransform: 'none', fontWeight: 600 }}>Export</Button>
        <Button size="small" onClick={handleClick}><ArrowDropDownIcon /></Button>
      </ButtonGroup>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => { handleClose(); exportVehicles(); }}
        >
          Vehicles
        </MenuItem>
        <MenuItem
          onClick={() => { handleClose(); exportProviders(); }}
        >
          Service Providers
        </MenuItem>
        <MenuItem
          onClick={() => { handleClose(); setHistoryOpen(true); }}
        >
          Maintenance History
        </MenuItem>
      </Menu>

      <HistoryReportDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        vehicles={vehicles}
        providers={providers}
        onExport={async (params) => {
          setHistoryOpen(false);
          await exportHistory(params);
        }}
      />
    </>
  );
}
